import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Switch,
} from "@mui/material";
import { useState } from "react";
import useIsMobile from "./useIsMobile";
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { submitConsent } from "./ApiService";
import SafeHTML from "./SafeHTML";
import { useParams } from "react-router-dom";

const CookiesConsent = ({
  consentResponse,
  brandDetails,
  sessionId,
}: {
  consentResponse: any;
  brandDetails: any;
  sessionId: any;
}) => {
  const { version, source, uniqueId } = useParams();
  const isMobile = useIsMobile();
  const [consentData, setConsentData] = useState<any>(consentResponse);
  const [isLoading, setIsLoading] = useState(false);
  const [endSession, setEndSession] = useState(false);
  const [customiseConsent, setCustomiseConsent] = useState<any>(null);

  const startChatConversation = (actionObj: any) => {
    if (actionObj.action?.toLowerCase() === "customize") {
      setCustomiseConsent(actionObj);
    }
    else{
        handleConsent(actionObj);
    }
  };

    const handleConsent = async (data: any) => {
        let payload = {
            request_id: sessionId,
            action: data.action,
            consent_given: data.consent_given,
            consent_options: data.consent_options || [],
            timestamp: new Date().toISOString()
        };

        setIsLoading(true);
        try {
            const response = await submitConsent(payload, version);
            if (response) {
                setCustomiseConsent(null);
                setEndSession(true);
            }
        } 
        catch (error) {
            console.error(`Failed to process chat:`, error);
        } 
        finally {
            setIsLoading(false);
        }
    };

  const handleToggle = (id: string) => {
    const updatedConsentOptions = customiseConsent.consent_options.map(
      (option: any) => {
        if (option.option_id === id) {
          return { ...option, selected: !option.selected };
        }
        return option;
      }
    );
    const updatedConsentObject = {
      ...customiseConsent,
      consent_options: updatedConsentOptions,
    };
    setCustomiseConsent(updatedConsentObject);
  };

  return (
    <>
      {!endSession && consentData && consentData.message && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="absolute bg-white bottom-0 p-4 rounded-t-[20px] shadow-lg w-full max-h-[90vh] overflow-y-auto">
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                }}
              >
                <CircularProgress />
              </div>
            ) : (
              <>
                {!customiseConsent && (
                    <>
                        {consentData && consentData?.message?.title && (
                            <div className={`font-semibold text-gray1 text-[16px] mb-2`}>{consentData.message.title}</div>
                        )}
                        {consentData?.message?.body && (
                            <div className={`text-gray1 mt-2 text-[14px] mb-2`}><SafeHTML html={consentData.message.body} /></div>
                        )}
                        {consentData.actions?.length > 0 && (
                            <div className={`flex justify-start flex-wrap ${isMobile ? "gap-2" : "gap-4"}`}>
                                {consentData.actions.map((option: any, index: number) => (
                                    <div key={`options-wrapper-${index}`}>
                                        <Button
                                            variant="outlined"
                                            className="truncate"
                                            title={option.label}
                                            sx={{
                                            borderRadius: "9999px",
                                            height: isMobile ? "32px" : "40px",
                                            textTransform: "none",
                                            backgroundColor: brandDetails.chatButtonColor,
                                            borderColor: brandDetails.chatButtonStrokeColor,
                                            color: brandDetails.chatButtonTextColor,
                                            "&:hover": {
                                                backgroundColor: brandDetails.chatButtonColor,
                                                borderColor: brandDetails.chatButtonStrokeColor,
                                                opacity: 0.8,
                                            },
                                            }}
                                            onClick={() => startChatConversation(option)}
                                        >
                                            <div className="truncate">{option.label}</div>
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {customiseConsent?.action?.toLowerCase() === "customize" && (
                    <>
                    {customiseConsent?.aria_label && (
                      <div className="flex items-center gap-2 mb-2">
                        <IconButton aria-label="back" onClick={() => setCustomiseConsent(null)}>
                            <KeyboardBackspaceIcon />
                        </IconButton>
                        <div className={`font-semibold text-gray1 text-[16px]`}>{customiseConsent.aria_label}</div>
                      </div>
                    )}

                    <div className="space-y-4">
                      {customiseConsent.consent_options.map((consent: any, index: any) => (
                          <Card key={consent.option_id} variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                              <div className="flex justify-between items-center gap-4">
                                <div className="flex-1">
                                  <div className={`text-gray1 font-semibold mb-1 ${consent.required ? 'required': ''}`}>{consent.option_id}</div>
                                  <div className="text-gray1"><SafeHTML html={consent.option_text} /></div>
                                </div>
                                <Switch checked={consent.selected} required color="primary" onChange={() => handleToggle(consent.option_id)} />
                              </div>
                            </CardContent>
                          </Card>
                        )
                      )}

                      <div className="flex justify-center">
                        <Button
                          variant="outlined"
                          title={customiseConsent.label}
                          disabled={customiseConsent.consent_options.some(
                            (c: any) => c.required && !c.selected
                          )}
                          sx={{
                            borderRadius: "9999px",
                            height: isMobile ? "32px" : "40px",
                            textTransform: "none",
                            backgroundColor: brandDetails.chatButtonColor,
                            borderColor: brandDetails.chatButtonStrokeColor,
                            color: brandDetails.chatButtonTextColor,
                            "&:hover": {
                              backgroundColor: brandDetails.chatButtonColor,
                              borderColor: brandDetails.chatButtonStrokeColor,
                              opacity: 0.8,
                            },
                          }}
                          onClick={() => handleConsent(customiseConsent)}
                        >
                          <div className="truncate">{"Save"}</div>
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CookiesConsent;
